<template>
  <div
    class="tab-bar-wrapper shrink h-[30px] inline-flex flex-row gap-2 bg-white max-w-full flex-nowrap overflow-hidden"
    style="app-region: drag;">
    <div class="tab-bar" ref="tab-bar"
         :class="{ 'fade-left': (tabBar?.scrollLeft > 0) ?? false, 'fade-right': (tabBar?.scrollLeft + tabBar?.clientWidth < tabBar?.scrollWidth) ?? false }">
      <AppContextMenu
        v-for="(tab, idx) in sessionTabs"
        :key="tab.fullPath"
        class="inline-flex"
        style="app-region: no-drag;"
        :menu-items="[
        { text: 'Alle tabbladen sluiten', action: () => onCloseAllTabs() },
        { text: 'Rechter tabbladen sluiten', action: () => onCloseTabsToRight(idx) },
      ]"
      >
        <WindowTab
          v-bind="{ ...tab, active: $route.fullPath === tab.fullPath }"
          draggable="true"
          @dragover.prevent=""
          @dragstart="onDragStart(idx)"
          @dragend="onDragEnd"
          @drop="onDrop(idx)"
          @click="$router.push(tab.fullPath)"
          @click.middle="closeTab(idx, true)"
          @close="closeTab(idx, true)"
        />
      </AppContextMenu>
    </div>
    <VDropdown
      class="notification-bell flex-initial flex ml-auto h-full self-center"
      :class="{ 'pr-[150px]': isDesktop(), 'pr-2': !isDesktop()  }"
      style="app-region: no-drag;"
      :distance="10"
      :skidding="-64"
      v-model:shown="showNotifications"
    >
      <AppIconLayers class="text-3xl h-[0.875em]">
        <AppIcon :icon="['fal', 'bell']"></AppIcon>
        <AppIconLayersText class="text-4xl" size="lg" counter
                           :value="notifications.filter(n => n.readAt == null).length" position="top-right"/>
      </AppIconLayers>
      <template #popper>
        <div class="w-fit 2xl:w-[20vw] max-w-[400px] max-h-[400px] p-3 inline-flex flex-wrap overflow-y-auto">
          <div class="w-full flex gap-2">
            <AppButton type="button"
                       color="info"
                       class="font-medium"
                       icon="list-check"
                       :disabled="notifications.filter(n => n.readAt == null).length == 0"
                       @click="markAllNotifications">
              <span>Markeer als gelezen</span>
            </AppButton>
            <AppButton type="button"
                       class="font-medium ml-auto"
                       icon="list"
                       @click="openNotificationIndex">
              <span>Alle notificaties</span>
            </AppButton>
          </div>
          <div v-if="notifications.filter(n => n.readAt == null).length > 0" class="w-full">
            <AppAlert dismissable dismissable-icon="eye" :has-details="hasDetails(notification)"
                      has-details-icon="comment" @dismiss="onDismissNotification(notification)"
                      @read-details="readDetailsNotification(notification)"
                      :type="notification.type.toLowerCase()"
                      v-for="notification in notifications.filter(n => n.readAt == null).slice(0, 6)">
              {{ utcDate(notification.createdAt).format("DD-MM-YYYY - HH:mm") }}<br>
              <b>{{ notification.title }}</b><br>
              <p v-if="notification.message" v-text="notification.message"></p>
              <p v-if="showDetails.includes(notification.id)">
                <div v-for="detail in notification.details">
                  <p>{{ detail }}</p><br>
                </div>
              </p>
            </AppAlert>
          </div>
          <AppAlert type="info" inline class="select-none mt-1"
                    v-if="notifications.filter(n => n.readAt == null).length == 0">
            Er zijn geen ongelezen notificaties.
          </AppAlert>
        </div>
      </template>
    </VDropdown>
  </div>
</template>

<script setup lang="ts">
import { useAppTabs } from "~/stores/useAppTabs";
import { useUserStore } from "~stores/User.ts";
import { Notification, markNotificationAsRead } from "@q-food/q-food";
import { utcDate } from "~utils/Date.ts";

const { push } = useRouter();

import { isDesktop } from "~utils/RuntimeEnvironment";

const { notifications } = storeToRefs(useUserStore());
const { sessionTabs, removeTab } = useAppTabs();

const fadeLeft = ref(false);
const fadeRight = ref(false);
const draggingIndex = ref(null as number | null);
const showDetails = ref<string[]>([]);
const showNotifications = ref<boolean>(false);

const { currentRoute } = useRouter();
const tabBar = useTemplateRef("tab-bar");

function onDragStart(index: number) {
	draggingIndex.value = index;
}

function openNotificationIndex() {
	showNotifications.value = false;
	push("/user-notification-log/index");
}

function onDragEnd() {
	draggingIndex.value = null;
}

const updateFades = () => {
	if (!tabBar.value) return;

	const el = tabBar.value;
	fadeLeft.value = el.scrollLeft > 0;
	fadeRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth;
};

function onDrop(index: number) {
	if (draggingIndex.value == null) return;

	swapArrayElements(sessionTabs.value, draggingIndex.value, index);

	draggingIndex.value = null;
}

function closeTab(tabIndex: number, activeTabCheck = false) {
	const tabRoute = sessionTabs.value[tabIndex].fullPath;

	if (tabRoute in window.ontabclose) {
		const closeTabEvent = {
			prevent: false,
		};

		try {
			window.ontabclose[tabRoute](closeTabEvent);
		} catch (e) {
			closeTabEvent.prevent = false;
		}

		if (closeTabEvent.prevent) {
			return;
		}

		delete window.ontabclose[tabRoute];
	}

	const activeTab = activeTabCheck && currentRoute.value.fullPath === tabRoute;

	removeTab(tabIndex, activeTab);
}

function onCloseAllTabs() {
	sessionTabs.value = [];
	push("/");
}

async function onCloseTabsToRight(fromIndex) {
	while (sessionTabs.value.length - 1 > fromIndex) {
		closeTab(fromIndex + 1);
	}
}

const swapArrayElements = (array: any[], index1: number, index2: number) => {
	// Checking parameters for errors
	if (index1 < 0 || index2 < 0 || index1 >= array.length || index2 >= array.length) {
		console.error("Invalid index passed to swapArrayElements()");

		return array;
	}

	// Swapping elements and returning the mutated array
	[array[index1], array[index2]] = [array[index2], array[index1]];

	return array;
};

async function markAllNotifications() {
	const userStore = useUserStore();
	await userStore.readAllNotifications();
}

async function onDismissNotification(notification: Notification) {
	notification.readAt = utcDate().format();
	await markNotificationAsRead(notification.id);
}

function readDetailsNotification(notification: Notification) {
	if (showDetails.value.includes(notification.id)) {
		showDetails.value = showDetails.value.filter((id) => id !== notification.id);
	} else {
		showDetails.value.push(notification.id);
	}
}

function hasDetails(notification: Notification) {
	return notification.details.length > 0;
}
</script>

<style scoped></style>
